import { useQuery } from '@tanstack/react-query';
import jwt_decode from 'jwt-decode';
import { trackError } from '@proposify/frontend-observability';
import { QueryKeys } from 'utils/queryKeys';
import { apiDocumentServiceRepository } from '../services/repositories/implementations/ApiDocumentServiceRepository.ts';
import type { GenerateTokenResponseDto } from '@proposify/document-service-api-schema';

// Default fallback if token parsing fails - 30 minutes
const DEFAULT_REFETCH_INTERVAL_MS = 1000 * 60 * 30;
// Refetch interval percentage - 80% of the token TTL
const REFETCH_INTERVAL_PERCENTAGE = 80;

const getTokenPayload = (token: string): { iat: number; exp: number } | null => {
  try {
    return jwt_decode<{ iat: number; exp: number }>(token);
  } catch (error) {
    trackError(error);
    return null;
  }
};

type TokenPayload = { iat: number; exp: number };

const isPayloadExpired = (payload: TokenPayload): boolean => Date.now() >= payload.exp * 1000;

const getTokenTTL = (payload: TokenPayload): number => (payload.exp - payload.iat) * 1000;

export const isTokenInvalid = (token: string): boolean => {
  const payload = getTokenPayload(token);
  if (!payload) return true;
  return isPayloadExpired(payload);
};

const calculateRefetchInterval = (data?: GenerateTokenResponseDto): number => {
  if (!data?.token) return DEFAULT_REFETCH_INTERVAL_MS;

  const payload = getTokenPayload(data.token);
  if (!payload) return DEFAULT_REFETCH_INTERVAL_MS;

  return getTokenTTL(payload) * (REFETCH_INTERVAL_PERCENTAGE / 100);
};

export const useTiptapAiToken = () => {
  const { data, isLoading, isError, isFetched } = useQuery({
    queryKey: QueryKeys.getTiptapAiToken,
    queryFn: () => apiDocumentServiceRepository.getTipTapAiToken(),
    staleTime: Infinity,
    refetchInterval: (query): number => {
      const data = query.state.data;
      return calculateRefetchInterval(data);
    },
  });

  const payload = data?.token ? getTokenPayload(data.token) : null;
  const validData = payload && !isPayloadExpired(payload) ? data : undefined;

  return {
    data: validData,
    isLoading,
    isError,
    isFetched,
  };
};
